import React, { useState, useEffect } from 'react';
import { Layout, Menu, Card, Row, Col, Statistic, DatePicker, Button, Space, Typography, Tooltip } from 'antd';
import {
    DashboardOutlined,
    UserOutlined,
    FileTextOutlined,
    BarChartOutlined,
    LogoutOutlined,
    ReloadOutlined,
    AppstoreOutlined,
    EditOutlined,
    QuestionCircleOutlined,
    NotificationOutlined,
    PictureOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import TemplateAnalytics from '../components/TemplateAnalytics';
import UserRetention from '../components/UserRetention';
import WritingTimeAnalytics from '../components/RetrospectWritingAnalytics';
import SpaceAnalytics from '../components/SpaceAnalytics';
import RegistrationAnalytics from '../components/RegistrationAnalytics';
import RetrospectCycleAnalytics from '../components/RetrospectCycleAnalytics';
import NoticeManagement from '../components/NoticeManagement';
import PopupManagement from '../components/PopupManagement';
import './DashboardPage.css';
import api from '../utils/api';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const DashboardPage = () => {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const [selectedMenu, setSelectedMenu] = useState('overview');
    const [dateRange, setDateRange] = useState([dayjs().subtract(30, 'day'), dayjs()]);
    const [loading, setLoading] = useState(false);
    const [outlineStats, setOutlineStats] = useState({
        totalMemberCount: null,
        totalSpaceCount: null,
        totalRetrospectCount: null,
        totalRetrospectAnswerCount: null,
    });

    useEffect(() => {
        const fetchOutlineStats = async () => {
            setLoading(true);
            try {
                const res = await api.get('/admin/outline');
                setOutlineStats(res.data);
            } catch (error) {
                setOutlineStats({
                    totalMemberCount: null,
                    totalSpaceCount: null,
                    totalRetrospectCount: null,
                    totalRetrospectAnswerCount: null,
                });
                console.error('개요 통계 데이터 로딩 실패:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchOutlineStats();
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleRefresh = () => {
        setLoading(true);
        // 데이터 새로고침 로직
        setTimeout(() => setLoading(false), 1000);
    };

    const menuItems = [
        {
            key: 'overview',
            icon: <DashboardOutlined />,
            label: '개요',
        },
        {
            key: 'templates',
            icon: <FileTextOutlined />,
            label: '템플릿 분석',
        },
        {
            key: 'retention',
            icon: <BarChartOutlined />,
            label: '리텐션 분석',
        },
        {
            key: 'writing-time',
            icon: <BarChartOutlined />,
            label: '회고 작성 분석',
        },
        {
            key: 'spaces',
            icon: <UserOutlined />,
            label: '스페이스 분석',
        },
        {
            key: 'registration',
            icon: <UserOutlined />,
            label: '회원가입 분석',
        },
        {
            key: 'creation-cycle',
            icon: <BarChartOutlined />,
            label: '회고 생성 주기',
        },
        {
            key: 'notices',
            icon: <NotificationOutlined />,
            label: '공지사항 관리',
        },
        {
            key: 'popups',
            icon: <PictureOutlined />,
            label: '팝업 관리',
        },
    ];

    const renderContent = () => {
        switch (selectedMenu) {
            case 'overview':
                return (
                    <div className="overview-content">
                        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                            ※ 아래 4개의 데이터는 날짜와 상관없이 전체 누적 카운트입니다.
                        </Text>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} lg={6}>
                                <Card>
                                    <Statistic
                                        title={<span>총 사용자 <Tooltip title="현재까지 가입한 전체 회원 수예요. (날짜 무관 전체 누적)"><QuestionCircleOutlined style={{ color: '#8A98A6', fontSize: 12 }} /></Tooltip></span>}
                                        value={outlineStats.totalMemberCount !== null ? outlineStats.totalMemberCount : '-'}
                                        prefix={<UserOutlined />}
                                        loading={loading}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <Card>
                                    <Statistic
                                        title={<span>총 스페이스 수 <Tooltip title="현재까지 생성된 전체 스페이스 수예요. 팀·개인 스페이스를 모두 포함해요. (날짜 무관 전체 누적)"><QuestionCircleOutlined style={{ color: '#8A98A6', fontSize: 12 }} /></Tooltip></span>}
                                        value={outlineStats.totalSpaceCount !== null ? outlineStats.totalSpaceCount : '-'}
                                        prefix={<AppstoreOutlined />}
                                        loading={loading}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <Card>
                                    <Statistic
                                        title={<span>총 회고 수 <Tooltip title="현재까지 생성된 전체 회고 수예요. created_at 기준으로 집계해요. (날짜 무관 전체 누적)"><QuestionCircleOutlined style={{ color: '#8A98A6', fontSize: 12 }} /></Tooltip></span>}
                                        value={outlineStats.totalRetrospectCount !== null ? outlineStats.totalRetrospectCount : '-'}
                                        prefix={<FileTextOutlined />}
                                        loading={loading}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <Card>
                                    <Statistic
                                        title={<span>총 회고 답변 수 <Tooltip title="현재까지 기록된 전체 회고 답변 수예요. 회고 1건당 질문 수만큼 답변이 생성돼요. (날짜 무관 전체 누적)"><QuestionCircleOutlined style={{ color: '#8A98A6', fontSize: 12 }} /></Tooltip></span>}
                                        value={outlineStats.totalRetrospectAnswerCount !== null ? outlineStats.totalRetrospectAnswerCount : '-'}
                                        prefix={<EditOutlined />}
                                        loading={loading}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
                            <Col xs={24} lg={12}>
                                <TemplateAnalytics dateRange={dateRange} />
                            </Col>
                        </Row>
                    </div>
                );
            case 'templates':
                return <TemplateAnalytics dateRange={dateRange} fullWidth />;
            case 'retention':
                return <UserRetention dateRange={dateRange} fullWidth />;
            case 'writing-time':
                return <WritingTimeAnalytics dateRange={dateRange} />;
            case 'spaces':
                return <SpaceAnalytics dateRange={dateRange} />;
            case 'registration':
                return <RegistrationAnalytics dateRange={dateRange} />;
            case 'creation-cycle':
                return <RetrospectCycleAnalytics dateRange={dateRange} />;
            case 'notices':
                return <NoticeManagement />;
            case 'popups':
                return <PopupManagement />;
            default:
                return <div>페이지를 선택해주세요.</div>;
        }
    };

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Sider width={220} className="dashboard-sider">
                <div className="logo">
                    <h2>Layer 어드민</h2>
                </div>
                <Menu
                    mode="inline"
                    selectedKeys={[selectedMenu]}
                    items={menuItems}
                    onClick={({ key }) => setSelectedMenu(key)}
                    className="dashboard-menu"
                />
            </Sider>

            <Layout>
                <Header className="dashboard-header">
                    <div className="header-content">
                        <div className="header-left">
                            <Title level={4} style={{ margin: 0 }}>
                                {menuItems.find(item => item.key === selectedMenu)?.label}
                            </Title>
                        </div>
                        <div className="header-right">
                            <Space>
                                {[30, 60, 90].map((days) => (
                                    <Button
                                        key={days}
                                        size="small"
                                        type={
                                            dateRange &&
                                            Math.round(dateRange[1].diff(dateRange[0], 'day', true)) === days
                                                ? 'primary'
                                                : 'default'
                                        }
                                        onClick={() => setDateRange([dayjs().subtract(days, 'day'), dayjs()])}
                                    >
                                        {days}일
                                    </Button>
                                ))}
                                <RangePicker
                                    value={dateRange}
                                    onChange={setDateRange}
                                    format="YYYY-MM-DD"
                                />
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={handleRefresh}
                                    loading={loading}
                                >
                                    새로고침
                                </Button>
                                <Button
                                    icon={<LogoutOutlined />}
                                    onClick={handleLogout}
                                    danger
                                >
                                    로그아웃
                                </Button>
                            </Space>
                        </div>
                    </div>
                </Header>

                <Content className="dashboard-content">
                    {renderContent()}
                </Content>
            </Layout>
        </Layout>
    );
};

export default DashboardPage;
